import ConfigurationHeader from "../../components/heading/ConfigurationHeader";
import Input, { DashboardCheckbox } from "../../components/Input/Input";
import QuestionHeading from "../../components/heading/QuestionHeading";
import { IndentWrapper } from "../orderConfiguration/OrderConfiguration";
import "./PasswordValidations.css";
import React, {useEffect, useState } from "react";
import { Button } from "@mui/material";
import { validateWithOtp } from "../../stateManagement/actions/validateOtpPopupActions";
import { openPopup } from "../../stateManagement/actions/popupActions";
import { doFinalConfigSubmit, getMetaDataConfig, postConfigWithDomainName, updateConfigWithDomainName, updatePasswordCheckProperies } from "../../services/uiConfigService";
import { manageUpdateAccessObj } from "../manageUpdateAccess/manageUpdateAccessTypes";

const checkBoxKeys = ["upper","lower","numeric","special","prevPasswordCheck","length"];

const defaults = {
  minLength: 8,
  maxLength: 20
}

const PasswordValidation = (props: { showBackButton?: boolean }) => {
  const [options, setOptions] = useState<string[]>([]);
  const [minLength, setMinLength] = useState<number>(defaults.minLength);
  const [maxLength,setMaxLength] = useState<number>(defaults.maxLength);
  const [generatedRegex, setGeneratedRegex] = useState<string>("");
  const [message, setGenericMessage] = useState<string>('');
  const [prevPasswordAllowed,setPrevPasswordAlowed] = useState<string>("1");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setGenericMessage(e.target.value);
  };
  useEffect(() => {
    async function getAndSetPasswordComplexity(){
      const configName = "passwordComplexity";
      const passwordComplexity = (await getMetaDataConfig("password",configName))?.domainValues;
      const passwordComplexityObj = passwordComplexity?.[0];
      if(passwordComplexityObj){
        const selectedCheckBoxes:string[] = [];
        checkBoxKeys.forEach((key) => {
          if(passwordComplexityObj[key]){
            selectedCheckBoxes.push(key);
          }
        })
        setOptions(selectedCheckBoxes);
        setGenericMessage(passwordComplexityObj?.message ?? "");
        if(passwordComplexityObj?.prevPasswordCount) setPrevPasswordAlowed(passwordComplexityObj?.prevPasswordCount);
        if(passwordComplexityObj?.minLength) setMinLength(parseInt(passwordComplexityObj?.minLength))
        if(passwordComplexityObj?.maxLength) setMaxLength(parseInt(passwordComplexityObj?.maxLength))
      }
    }
    getAndSetPasswordComplexity();
  },[])
  useEffect(() => {
    generateRegex();
  },[options,minLength,maxLength])

  const generateRegex = () => {
    let regex = "";

    if (options.includes("upper")) {
      regex += "(?=.*[A-Z])";
    }

    if (options.includes("lower")) {
      regex += "(?=.*[a-z])";
    }

    if (options.includes("numeric")) {
      regex += "(?=.*\\d)";
    }

    if (options.includes("special")) {
      regex += "(?=.*[@#$%^&+=])";
    }

    // if(minLength && minLength>0) regex += ".{" + minLength + ",}$";
    if (options.includes("length") && minLength && minLength > 0) {
      regex += ".{" + minLength;
      if (maxLength && maxLength > minLength) {
        regex += "," + maxLength;
      }
      regex += "}$";
    }
    setGeneratedRegex(regex);
  };
  function validateConfigData(){
    if(generatedRegex && !message){
      return "Popup message can't be empty"
    }
    if(options.includes("length")){
      if(!minLength || !maxLength){
        return "Password minimum or maxmimum length can't be empty"
      }
      if(minLength<defaults.minLength){
        return `Password minimum length can't be less than ${defaults.minLength}`
      }
      if(maxLength>defaults.maxLength){
        return `Password maximum length can't be more than ${defaults.maxLength}`
      }
      if(maxLength<minLength){
        return "Maximum length can't be less than Minimum length";
      }
    }
    if(parseInt(prevPasswordAllowed)<=0){
      return "Number of previous passwords to be checked can't be less than or equal to zero "
    }
    return "";
  }
  function getValidationKeys(){
    const validationKeysObj: { [key: string]: string | boolean }  = {};
    options.forEach((keyValue) => {
      validationKeysObj[keyValue] = true;
    })
    return validationKeysObj;
  }
  const saveConfiguration = async (preview?: boolean) => {
    const validationMessage = validateConfigData();
    if(validationMessage){
      openPopup("Alert",validationMessage);
      return;
    }
      try{
        const configName = "passwordComplexity";
        const validationKeys = getValidationKeys();
        let configObj = {
          regex: generatedRegex || ".*",
          message: message,
          designations: "*",
          prevPasswordCount: prevPasswordAllowed,
          minLength,
          maxLength,
          ...validationKeys
        }
        const configValue = [configObj];
        await doFinalConfigSubmit(configName,configValue,undefined,"password");
        const prevPasswordCheck = options.includes("prevPasswordCheck");
        await updatePasswordCheckProperies("old.password.save.check",prevPasswordCheck + "");
        if(prevPasswordCheck){
          await updatePasswordCheckProperies("old.saved.password.count",prevPasswordAllowed);
        }
        openPopup("Success","Configuration updated successfully");
      }catch(err){
        let message = "Something went wrong";
        if(err instanceof Error){
          message = err?.message
        }
        openPopup("Error",message);
      }
  }

  const handleCheckboxChange = (option: string) => {
    const updatedOptions = options.includes(option)
      ? options.filter((item: string) => item !== option)
      : [...options, option];
    setOptions(updatedOptions);
  };

  const handleMinLengthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const length = parseInt(e.target.value);
    setMinLength(length);
  };
  const handleMaxLengthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newLength = parseInt(e.target.value);
    setMaxLength(newLength)
  }
  const handlePrevPasswordAllowed = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPrevPasswordAlowed(e.target.value);
  };

  return (
    <div className="auth_config_container">
      <ConfigurationHeader
        heading={"Password Complexity"}
        showBackButton={props.showBackButton}
        saveConfiguration={() => saveConfiguration(false)}
      />
      <div>
        <IndentWrapper indent={1}>
          <QuestionHeading disableTextTransform={true}>
            Require at least one Upper case letter [A-Z]
          </QuestionHeading>
          <div className="answer_container">
            <DashboardCheckbox
              onChange={() => handleCheckboxChange("upper")}
              name="instantRegistration"
              label="Yes"
              checked={options.includes("upper")}
            />
          </div>
        </IndentWrapper>
        <IndentWrapper indent={1}>
          <QuestionHeading>
            Require at least one Lower case letter [a-z]
          </QuestionHeading>
          <div className="answer_container">
            <DashboardCheckbox
              onChange={() => handleCheckboxChange("lower")}
              name="instantRegistration"
              label="Yes"
              checked={options.includes("lower")}
            />
          </div>
        </IndentWrapper>
        <IndentWrapper indent={1}>
          <QuestionHeading>
            Require at least one numeric entity [0-9]
          </QuestionHeading>
          <div className="answer_container">
            <DashboardCheckbox
              onChange={() => handleCheckboxChange("numeric")}
              name="instantRegistration"
              label="Yes"
              checked={options.includes("numeric")}
            />
          </div>
        </IndentWrapper>

        <IndentWrapper indent={1}>
          <QuestionHeading>
            Require at least one Special Character (@#$%^&+=)
          </QuestionHeading>
          <div className="answer_container">
            <DashboardCheckbox
              onChange={() => handleCheckboxChange("special")}
              name="instantRegistration"
              label="Yes"
              checked={options.includes("special")}
            />
          </div>
        </IndentWrapper>

        <IndentWrapper indent={1}>
          <QuestionHeading>
            Require length validation
          </QuestionHeading>
          <div className="answer_container">
            <DashboardCheckbox
              onChange={() => handleCheckboxChange("length")}
              name="instantRegistration"
              label="Yes"
              checked={options.includes("length")}
            />
          </div>
          {options.includes("length") && <>
          `<IndentWrapper indent={1}>
            <QuestionHeading>
              Set minimum length (default: {defaults.minLength}, range: {defaults.minLength}-{defaults.maxLength})
            </QuestionHeading>
            <div className="answer_container">
              <div className="answer_container">
                <Input
                  type="number"
                  onChange={handleMinLengthChange}
                  value={minLength}
                  name="minOrder"
                />
              </div>
            </div>
          </IndentWrapper>

          <IndentWrapper indent={1}>
            <QuestionHeading>
              Set maximum length (default: {defaults.minLength}, range: {defaults.minLength}-{defaults.maxLength})
            </QuestionHeading>
            <div className="answer_container">
              <div className="answer_container">
                <Input
                  type="number"
                  onChange={handleMaxLengthChange}
                  value={maxLength}
                  name="minOrder"
                />
              </div>
            </div>
          </IndentWrapper>`
          </>}
        </IndentWrapper>

        <IndentWrapper indent={1}>
          <QuestionHeading>
           Validate for previous passwords
          </QuestionHeading>
          <div className="answer_container">
            <DashboardCheckbox
              onChange={() => handleCheckboxChange("prevPasswordCheck")}
              name="instantRegistration"
              label="Yes"
              checked={options.includes("prevPasswordCheck")}
            />
          </div>
        </IndentWrapper>

        {options.includes("prevPasswordCheck") && <IndentWrapper indent={1}>
          <QuestionHeading>
            Number of previous password to be checked
          </QuestionHeading>
          <div className="answer_container">
            <div className="answer_container">
              <Input
                type="number"
                onChange={handlePrevPasswordAllowed}
                value={prevPasswordAllowed}
                name="minOrder"
              />
            </div>
          </div>
        </IndentWrapper>}

        <IndentWrapper indent={1}>
          <QuestionHeading>Enter Pop Up Message</QuestionHeading>
          <div className="answer_container">
            <div className="answer_container">
              <Input
                type="text"
                onChange={handleChange}
                value={message}
                name="popUpMessage"
              />
            </div>
          </div>
        </IndentWrapper>

        {/* <br />
        <Button
          className="manage-event-save-button"
          variant="contained"
          sx={{ backgroundColor: "#23c6b0" }}
          onClick={generateRegex}
        >
          Generate Regex
        </Button> */}
      </div>
      {/* {generatedRegex && (
        <div>
          <h3>Generated Regex for Password Validation:</h3>
          <pre>{generatedRegex}</pre>
          <h3>Generated Messages for Password Validation:</h3>
          <pre>{message}</pre>
        </div>
      )} */}
    </div>
  );
};
export default PasswordValidation;
